import { GatsbyLinkProps, Link as GatsbyLink } from 'gatsby'
import React, { forwardRef } from 'react'

import { usePageUrl } from '../../lib/pages/hooks/usePageUrl'
import { normalizeLink } from '../../lib/pages/links'
import { PageUrlOptions } from '../../lib/pages/types'

// https://www.polynique.com/web-development/extend-gatsby-link-in-typescript/
// https://github.com/gatsbyjs/gatsby/issues/16682
// https://github.com/gatsbyjs/gatsby/issues/27035
export type LinkProps = Omit<GatsbyLinkProps<Record<string, unknown>>, 'ref' | 'to'> &
  PageUrlOptions & {
    to: GatsbyLinkProps<Record<string, unknown>>['to'] | undefined
    'aria-label'?: string
  }

export type LinkRef = HTMLAnchorElement & GatsbyLink<Record<string, unknown>>

const Link = forwardRef<LinkRef, LinkProps>(function Link(
  {
    children,
    to,
    activeClassName,
    partiallyActive,
    forceBase,
    'aria-label': ariaLabelProp,
    ...otherProps
  },
  ref,
) {
  const { getPagePath } = usePageUrl()
  const { target, rel, state, ...remainingAnchorProps } = otherProps

  const link = normalizeLink(to, { target })

  const defaultLabel =
    typeof children === 'string' && children.trim() !== ''
      ? children.trim()
      : typeof to === 'string'
        ? to
        : link.type === 'gatsby'
          ? link.to
          : link.href ?? undefined

  const computedLabel =
    ariaLabelProp ??
    (typeof children === 'string'
      ? children.trim() === defaultLabel
        ? `Link to ${defaultLabel}`
        : children.trim()
      : undefined)

  const ariaLabel = computedLabel === '/' ? 'Designs for Health Homepage' : computedLabel

  if (link.type === 'gatsby') {
    return (
      <GatsbyLink
        to={getPagePath(link.to, { forceBase })}
        activeClassName={activeClassName}
        partiallyActive={partiallyActive}
        ref={ref}
        aria-label={ariaLabel}
        {...otherProps}
      >
        {children}
      </GatsbyLink>
    )
  }

  const targetProps = {
    ...(link.blank ? { target: '_blank' } : {}),
    ...(target ? { target } : {}),
  }

  const relProps = {
    ...(targetProps.target === '_blank' ? { rel: 'noopener' } : {}),
    ...(rel ? { rel } : {}),
  }

  const anchorProps = {
    ...(link.href ? { href: link.href } : {}),
    ...targetProps,
    ...relProps,
    ...remainingAnchorProps,
  }

  return (
    <a ref={ref} aria-label={ariaLabel} {...anchorProps}>
      {children}
    </a>
  )
})

export default Link
